// src/App.js - IMPROVED WITH TIMEOUT & ERROR HANDLING + PWA FIX
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CurrencyProvider, useCurrency } from './context/CurrencyContext';
import Landing from './pages/Landing/Landing';
import Dashboard from './pages/Dashboard/Dashboard';
import Stocks from './pages/Stocks/Stocks';
import StockCategory from './pages/Stocks/StockCategory';
import StockDetail from './pages/StockDetail/StockDetail';
import WatchlistChart from './pages/WatchlistChart/WatchlistChart';
import InvestmentStocks from './pages/Investment/InvestmentStocks';
import InvestmentPlansPage from './pages/Investment/InvestmentPlansPage';
import Profile from './pages/Profile/Profile';
import Portfolio from './pages/Portfolio/Portfolio';
import TransactionHistory from './pages/TransactionHistory/TransactionHistory';
import VerifyEmail from './pages/Auth/VerifyEmail';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Deposit from './pages/Deposit/Deposit';
import Withdraw from './pages/Withdraw/Withdraw';
import WithdrawPin from './pages/Withdraw/WithdrawPin';
import PlanWithdrawPage from './pages/Withdraw/PlanWithdrawPage';
import PlanLanding from './pages/Plan/PlanLanding';
import PlanQuestionnairePage from './pages/Plan/PlanQuestionnairePage';
import ManualContribution from './pages/Plan/ManualContribution';
import LevelsInfoPage from './pages/Wallet/LevelsInfoPage';
import PendingOrders from './pages/PendingOrders/PendingOrders';
import Navigation from './components/common/Layout/Navigation';
import NewsDetail from './pages/NewsDetail/NewsDetail';
import VideoDetail from './pages/VideoDetail/VideoDetail';
import CopyTradingPage from './pages/CopyTrading/CopyTradingPage';
import Terms from './pages/Terms/Terms';

import './App.css';

// Loading timeout constant (8 seconds to match context timeouts)
const LOADING_TIMEOUT = 8000;

const LoadingScreen = ({ error, onRetry }) => (
  <div className="loading-screen">
    {error ? (
      <div className="loading-error">
        <div className="loading-error-icon">⚠️</div>
        <div className="loading-error-text">{error}</div>
        <button className="loading-retry-btn" onClick={onRetry}>
          Retry
        </button>
      </div>
    ) : (
      <div className="loading-content">

        {/* Animated logo — 3 bars fill like water, matching exact logo proportions */}
        <div className="splash-logo">

          {/* Left bar: medium height, sits slightly above bottom */}
          <div className="splash-bar-wrap bar-left">
            <div className="splash-bar-fill fill-left">
              <div className="splash-wave" />
            </div>
          </div>

          {/* Center bar: tallest, anchored at bottom */}
          <div className="splash-bar-wrap bar-center">
            <div className="splash-bar-fill fill-center">
              <div className="splash-wave" />
            </div>
          </div>

          {/* Right bar: shorter, floats higher — bottom doesn't reach canvas bottom */}
          <div className="splash-bar-wrap bar-right">
            <div className="splash-bar-fill fill-right">
              <div className="splash-wave" />
            </div>
          </div>

        </div>

      </div>
    )}
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { loading: currencyLoading, currency } = useCurrency();
  const [loadingError, setLoadingError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  useEffect(() => {
    // Mark initial load as complete once both have loaded
    if (!authLoading && !currencyLoading && !initialLoadComplete) {
      setInitialLoadComplete(true);
      console.log('✅ Initial load complete - won\'t show loading screen on navigation anymore');
    }
  }, [authLoading, currencyLoading, initialLoadComplete]);
  
  useEffect(() => {
    // Only set timeout if we haven't completed initial load
    if (initialLoadComplete) return;
    
    // Set timeout for loading - should never be needed with fixed contexts
    const timeout = setTimeout(() => {
      if (authLoading || currencyLoading) {
        console.error('⏱️ App-level loading timeout reached', { authLoading, currencyLoading });
        setLoadingError('Loading is taking longer than expected. Please check your connection.');
      }
    }, LOADING_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [authLoading, currencyLoading, retryKey, initialLoadComplete]);

  const handleRetry = () => {
    setLoadingError(null);
    setRetryKey(prev => prev + 1);
    window.location.reload();
  };
  
  console.log('🛡️ ProtectedRoute check:', {
    authLoading,
    currencyLoading,
    currency,
    isAuthenticated,
    initialLoadComplete,
    retryKey
  });
  
  // Show error state
  if (loadingError) {
    return <LoadingScreen error={loadingError} onRetry={handleRetry} />;
  }
  
  // ONLY show loading screen on INITIAL load, not on subsequent navigations
  if (!initialLoadComplete && (authLoading || currencyLoading)) {
    console.log('⏳ Initial load in progress...', { authLoading, currencyLoading });
    return <LoadingScreen />;
  }
  
  console.log('✅ Both loaded, rendering with currency:', currency);
  
  return isAuthenticated ? (
    <div className="app-layout">
      <Navigation />
      <main className="main-content">
        {children}
      </main>
    </div>
  ) : <Navigate to="/" replace />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const [loadingError, setLoadingError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.error('⏱️ Auth loading timeout');
        setLoadingError('Authentication check failed. Please try again.');
      }
    }, LOADING_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [loading, retryKey]);

  const handleRetry = () => {
    setLoadingError(null);
    setRetryKey(prev => prev + 1);
    window.location.reload();
  };
  
  if (loadingError) {
    return <LoadingScreen error={loadingError} onRetry={handleRetry} />;
  }
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🔥 Error Boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>💥</div>
          <h2 style={{ marginBottom: '10px' }}>Something went wrong</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Reload App
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function AppContent() {
  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/" 
          element={
            <PublicRoute>
              <Landing />
            </PublicRoute>
          } 
        />
        <Route 
          path="/signup" 
          element={
            <PublicRoute>
              <Landing signupMode={true} />
            </PublicRoute>
          } 
        />

        {/* 
          Auth utility routes — NOT wrapped in PublicRoute or ProtectedRoute.
          Reason: These must be accessible regardless of login state.
          - A logged-out user needs /forgot-password and /reset-password to recover access.
          - A logged-in user clicking an old reset link in their email should also be
            able to land on /reset-password without being redirected to /dashboard.
          - /verify-email is already handled the same way for the same reason.
        */}
        <Route 
          path="/verify-email/:token" 
          element={<VerifyEmail />} 
        />
        <Route 
          path="/forgot-password" 
          element={<ForgotPassword />} 
        />
        <Route 
          path="/reset-password/:token" 
          element={<ResetPassword />} 
        />
        
        {/* Protected Routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/stocks" 
          element={
            <ProtectedRoute>
              <Stocks />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/stocks/category/:category" 
          element={
            <ProtectedRoute>
              <StockCategory />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/stocks/:symbol" 
          element={
            <ProtectedRoute>
              <StockDetail />
            </ProtectedRoute>
          } 
        />

        {/* ── Watchlist Chart Route ── */}
        <Route
          path="/watchlist/:symbol"
          element={
            <ProtectedRoute>
              <WatchlistChart />
            </ProtectedRoute>
          }
        />

        <Route 
          path="/investment/stocks" 
          element={
            <ProtectedRoute>
              <InvestmentStocks />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/investment/plans" 
          element={
            <ProtectedRoute>
              <InvestmentPlansPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/portfolio" 
          element={
            <ProtectedRoute>
              <Portfolio />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/transaction-history" 
          element={
            <ProtectedRoute>
              <TransactionHistory />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/deposit" 
          element={
            <ProtectedRoute>
              <Deposit />
            </ProtectedRoute>
          } 
        />

        {/* ── Withdraw PIN gate — must be BEFORE /withdraw/* ── */}
        <Route
          path="/withdraw/pin"
          element={
            <ProtectedRoute>
              <WithdrawPin />
            </ProtectedRoute>
          }
        />

        <Route 
          path="/withdraw/*" 
          element={
            <ProtectedRoute>
              <Withdraw />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/wallet/levels" 
          element={
            <ProtectedRoute>
              <LevelsInfoPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/pending-orders" 
          element={
            <ProtectedRoute>
              <PendingOrders />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/wallet/deposit" 
          element={
            <ProtectedRoute>
              <Deposit />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/plan" 
          element={
            <ProtectedRoute>
              <PlanLanding />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/plan/questionnaire" 
          element={
            <ProtectedRoute>
              <PlanQuestionnairePage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/plan/contribute/:planId" 
          element={
            <ProtectedRoute>
              <ManualContribution />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/withdraw/plan/:planId" 
          element={
            <ProtectedRoute>
              <PlanWithdrawPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/news/:slug" 
          element={
            <ProtectedRoute>
              <NewsDetail />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/video/:videoId" 
          element={
            <ProtectedRoute>
              <VideoDetail />
            </ProtectedRoute>
          } 
        />

        {/* ── Copy Trading Route ── */}
        <Route
          path="/copy-trading"
          element={
            <ProtectedRoute>
              <CopyTradingPage />
            </ProtectedRoute>
          }
        />

        {/* ── Terms & Conditions (dashboard entry point) ── */}
        <Route
          path="/terms"
          element={
            <ProtectedRoute>
              <Terms />
            </ProtectedRoute>
          }
        />
                
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  // Detect iOS standalone mode
  useEffect(() => {
    const isIOSStandalone = window.navigator.standalone === true;
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
    
    if (isIOSStandalone || isInStandaloneMode) {
      console.log('📱 Running in iOS PWA mode');
      document.body.classList.add('ios-standalone');
    }
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <CurrencyProvider>
            <AppContent />
          </CurrencyProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;